import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useReportePropuesta } from '@/hooks/usePropuestasPago';
import { descargarReporteProveedor, descargarReporteZip } from '@/lib/pdf-reporte-proveedor';
import { CircleNotch, FilePdf } from '@phosphor-icons/react';
import { useState } from 'react';
import { toast } from 'sonner';

interface Props {
  idPropuesta: number;
  open: boolean;
  onClose: () => void;
}

function formatoMonto(n: number, moneda: string | null): string {
  return `${moneda && moneda !== 'CRC' ? moneda : 'CRC'} ${n.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/** Reporte de pago por proveedor (PDF), emitido desde los datos congelados al validar la propuesta. */
export function ReporteProveedoresDialog({ idPropuesta, open, onClose }: Props) {
  const { data, isLoading, error } = useReportePropuesta(idPropuesta, open);
  const [generando, setGenerando] = useState<string | null>(null);
  const reporte = data?.data;

  async function descargar(clave: string, accion: () => Promise<void>) {
    setGenerando(clave);
    try {
      await accion();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo generar el PDF');
    } finally {
      setGenerando(null);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(abierto) => !abierto && onClose()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Reporte de pago por proveedor</DialogTitle>
          <DialogDescription>
            Un PDF por proveedor con las facturas que se pagan (N.º de factura del proveedor,
            fechas, montos y saldo). Descargá uno solo o todos en un ZIP con un archivo por
            proveedor. Usa los datos congelados al validar la propuesta.
          </DialogDescription>
        </DialogHeader>

        {isLoading && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
            Cargando…
          </div>
        )}
        {error && (
          <p className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'No se pudo cargar el reporte'}
          </p>
        )}

        {reporte && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <p className="text-sm text-muted-foreground">
                {reporte.proveedores.length} pago(s) a proveedores · fecha de pago{' '}
                {reporte.fechaPago ?? '—'}
              </p>
              <Button
                onClick={() => descargar('todos', () => descargarReporteZip(reporte))}
                disabled={generando !== null || reporte.proveedores.length === 0}
              >
                {generando === 'todos' ? (
                  <CircleNotch className="h-4 w-4 animate-spin" weight="bold" aria-hidden="true" />
                ) : (
                  <FilePdf className="h-4 w-4" weight="bold" aria-hidden="true" />
                )}
                Descargar todos (ZIP)
              </Button>
            </div>

            <div className="max-h-[50vh] overflow-auto rounded-md border border-border">
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-muted text-left">
                  <tr>
                    <th className="px-3 py-2 font-medium">Proveedor</th>
                    <th className="px-3 py-2 font-medium">Cuenta</th>
                    <th className="px-3 py-2 text-right font-medium">Facturas</th>
                    <th className="px-3 py-2 text-right font-medium">Total</th>
                    <th className="px-3 py-2" />
                  </tr>
                </thead>
                <tbody>
                  {reporte.proveedores.map((p) => {
                    const clave = `${p.vendorCode}|${p.cuentaBancoOrigen ?? ''}`;
                    return (
                      <tr key={clave} className="border-t border-border">
                        <td className="px-3 py-2">{p.vendorName ?? p.vendorCode}</td>
                        <td className="px-3 py-2 text-muted-foreground">
                          {p.cuentaBancoNombre ?? p.cuentaBancoOrigen ?? '—'}
                        </td>
                        <td className="px-3 py-2 text-right">{p.facturas.length}</td>
                        <td className="px-3 py-2 text-right font-mono">
                          {formatoMonto(p.totalPagado, p.currencyCode)}
                        </td>
                        <td className="px-3 py-2 text-right">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                              descargar(clave, () => descargarReporteProveedor(reporte, p))
                            }
                            disabled={generando !== null}
                          >
                            {generando === clave ? (
                              <CircleNotch
                                className="h-4 w-4 animate-spin"
                                weight="bold"
                                aria-hidden="true"
                              />
                            ) : (
                              <FilePdf className="h-4 w-4" weight="bold" aria-hidden="true" />
                            )}
                            PDF
                          </Button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
